/**
 * Where a dispatched work order went, and whether the contractor has it.
 *
 * Three steps lit in turn — sending (amber), delivered (blue), received
 * (green) — or a red line when nothing got through, with who was messaged
 * and how. The office can record a receipt that came by phone or in person;
 * the app and the confirm link record their own.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import s from "./WorkOrders.module.css";
import { useSession } from "../../app/SessionContext";
import { useConfirmReceipt, useSimulateContractor, useWorkOrderMessages } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { Button, Input, Select } from "../../design/components";
import {
  DELIVERY_HINT,
  DELIVERY_LABEL,
  DELIVERY_STEPS,
  DELIVERY_TONE,
  deliveryOf,
  RECEIPT_VIA_LABEL,
  type DeliveryState,
} from "../../domain/workOrderDelivery";
import { CHANNEL_LABEL } from "../../domain/contacts";
import type { ReceiptVia, WorkOrder } from "../../domain/workOrders";
import { contractorOf } from "../../domain/workOrderClass";
import { formatDateTime } from "../../lib/format";
import { t } from "../../i18n";

/** The small colored mark the list shows under an order's status. */
export function DeliveryMark({ wo }: { wo: WorkOrder }) {
  const state = deliveryOf(wo);
  if (!state) return null;
  return (
    <span className={s.deliveryMark} style={{ color: DELIVERY_TONE[state] }} title={t(DELIVERY_HINT[state])} data-delivery={state}>
      <span className={s.deliveryDot} style={{ background: DELIVERY_TONE[state] }} aria-hidden="true" />
      {t(DELIVERY_LABEL[state])}
    </span>
  );
}

const MANUAL: ReceiptVia[] = ["phone", "in_person", "email", "other"];

export function WorkOrderDelivery({ wo }: { wo: WorkOrder }) {
  const { principal, source } = useSession();
  const state = deliveryOf(wo);
  const messages = useWorkOrderMessages(wo.id);
  const confirm = useConfirmReceipt(wo.id);
  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const [via, setVia] = useState<ReceiptVia>("phone");
  const [by, setBy] = useState(contractorOf(wo) ?? "");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [noRole, setNoRole] = useState(false);

  if (!state) return null;

  const sent = (messages.data ?? []).filter((m) => (m.event ?? "dispatched") === "dispatched");
  const people = [...new Set(sent.filter((m) => m.status !== "no_address" || m.to).map((m) => m.recipient))];
  const channels = [...new Set(sent.filter((m) => m.status !== "no_address").map((m) => m.channel))];
  const missing = sent.filter((m) => m.status === "no_address" && m.error?.includes("contact directory"));
  const reached = (step: DeliveryState) =>
    state === "received" || (state === "delivered" && step !== "received") || (state === "sending" && step === "sending");

  return (
    <section className={s.delivery} style={{ borderInlineStartColor: DELIVERY_TONE[state] }} data-testid="wo-delivery" aria-label={t("Where it went")}>
      <div className={s.deliveryHead}>
        <b>{t("Where it went")}</b>
        <span style={{ color: DELIVERY_TONE[state], fontWeight: 600 }}>{t(DELIVERY_HINT[state])}</span>
      </div>

      {state === "nobody" || state === "not_delivered" ? null : (
        <ol className={s.deliverySteps}>
          {DELIVERY_STEPS.map((step) => (
            <li
              key={step}
              className={s.deliveryStep}
              data-on={reached(step)}
              style={reached(step) ? { background: DELIVERY_TONE[step], borderColor: DELIVERY_TONE[step] } : undefined}
            >
              {t(DELIVERY_LABEL[step])}
            </li>
          ))}
        </ol>
      )}

      <div className={s.deliveryFacts}>
        {wo.receivedAt && (
          <div>
            {t("Received {details} on {when}.", {
              details: [
                wo.receivedBy ? t("by {name}", { name: wo.receivedBy }) : "",
                wo.receivedVia ? t(RECEIPT_VIA_LABEL[wo.receivedVia]) : "",
              ]
                .filter(Boolean)
                .join(" "),
              when: formatDateTime(wo.receivedAt),
            })}
          </div>
        )}
        {people.length > 0 && (
          <div>
            {t("Sent to {people} by {channels}.", {
              people: people.join(", "),
              channels: channels.map((c) => t(CHANNEL_LABEL[c])).join(", "),
            })}
          </div>
        )}
        {missing.map((m) => (
          <div key={m.id} style={{ color: "var(--red)" }}>
            {t("{name} is not in the contact directory, so they were not told.", { name: m.recipient })}{" "}
            <Link to="/contacts">{t("Add them")}</Link>
          </div>
        ))}
        {state === "nobody" && (
          <div>
            {t("Name a contractor on this order, or add a rule in Who is told.")}{" "}
            <Link to="/notify-rules">{t("Who is told →")}</Link>
          </div>
        )}
        {source.kind === "mock" && state !== "received" && (
          <div className="muted">{t("Sample: the contractor's app confirms by itself about 12 seconds after dispatch.")}</div>
        )}
        {["dispatched", "in_progress", "on_hold"].includes(wo.status) && (
          <div className="muted">
            {t("The contractor can confirm, start and close the job, with a photo, from the link in their SMS or email.")}
          </div>
        )}
      </div>

      {!wo.receivedAt && (
        <form
          className={s.actionRow}
          onSubmit={(e) => {
            e.preventDefault();
            if (!canWrite) return setNoRole(true);
            setRefusal(null);
            confirm.mutate(
              { via, by },
              { onError: (err) => setRefusal(err instanceof Error ? err.message : String(err)) },
            );
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            {t("They confirmed another way?")}
          </span>
          <Select value={via} onChange={(e) => setVia(e.target.value as ReceiptVia)} aria-label={t("How they confirmed")}>
            {MANUAL.map((v) => (
              <option key={v} value={v}>
                {v === "other" ? t("Other") : t(RECEIPT_VIA_LABEL[v])}
              </option>
            ))}
          </Select>
          <Input
            value={by}
            onChange={(e) => setBy(e.target.value)}
            placeholder={t("Who confirmed")}
            aria-label={t("Who confirmed")}
            style={{ flex: 1, minWidth: 140 }}
          />
          <Button type="submit" size="sm" disabled={confirm.isPending}>
            {t("Mark as received")}
          </Button>
        </form>
      )}
      {source.simulateContractor && ["dispatched", "in_progress", "on_hold"].includes(wo.status) && (
        <ContractorPhone wo={wo} />
      )}
      {noRole && (
        <div className={s.refusal} role="alert">
          {t("You need the Supervisor or Technician role to change this.")}
        </div>
      )}
      {refusal && (
        <div className={s.refusal} role="alert">
          {source.kind === "mock"
            ? t("Refused by the sample: {reason}", { reason: refusal })
            : t("Refused by the platform: {reason}", { reason: refusal })}
        </div>
      )}
    </section>
  );
}

/**
 * The sample only: the page the contractor sees from the link, played here
 * because the sample has no phone at the other end. On the platform the same
 * three steps are served to the contractor's phone by /v1/receipt/{token}.
 */
function ContractorPhone({ wo }: { wo: WorkOrder }) {
  const act = useSimulateContractor(wo.id);
  const [open, setOpen] = useState(false);
  const [photo, setPhoto] = useState<File | undefined>();
  const [note, setNote] = useState("");
  if (!open)
    return (
      <div>
        <Button size="sm" onClick={() => setOpen(true)}>
          {t("Open the contractor's phone (sample)")}
        </Button>
      </div>
    );
  const started = wo.status !== "dispatched";
  return (
    <div className={s.phone} data-testid="wo-contractor-phone">
      <div className={s.phoneHead}>
        <b>{wo.number}</b> — {wo.title}
      </div>
      <ol className={s.deliverySteps}>
        {[
          [t("Received"), !!wo.receivedAt],
          [t("Started"), started],
          [t("Done"), false],
        ].map(([label, on]) => (
          <li key={String(label)} className={s.deliveryStep} data-on={on} style={on ? { background: "var(--green)", borderColor: "var(--green)" } : undefined}>
            {label}
          </li>
        ))}
      </ol>
      {!wo.receivedAt && (
        <Button size="sm" onClick={() => act.mutate({ action: "received" })} disabled={act.isPending}>
          {t("I received it")}
        </Button>
      )}
      {!started && (
        <Button size="sm" onClick={() => act.mutate({ action: "start" })} disabled={act.isPending}>
          {t("Start work")}
        </Button>
      )}
      <label className={s.phoneField}>
        {t("Photo of the finished work")}
        <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0])} />
      </label>
      <label className={s.phoneField}>
        {t("Note (optional)")}
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Button variant="primary" size="sm" onClick={() => act.mutate({ action: "done", photo, note })} disabled={act.isPending}>
        {t("Job done")}
      </Button>
    </div>
  );
}
